// ============================================================================
// useFinAlertas — contador da Auditoria do Financeiro (pacote 1, 07/10/2026)
// ============================================================================
// O menu (Sidebar: número ao lado de "Financeiro") e a tela do Financeiro
// (aba "Auditoria" + chip no topo) mostram quantos alertas graves e médios
// estão abertos (badge = alta + média, só os depois do início da vigilância).
//
// UMA loja no módulo: Sidebar e Finance dividem o mesmo pedido. Busca no
// primeiro uso, a cada 5 min com a aba visível e quando a janela volta ao
// foco (no máximo 1×/min). recarregar() força (depois de mudar um status).
// REGRA LINEAR: erro mantém o último número e preenche `erro` (nunca zera).
// 403 (não é operador) → contagem null, sem erro.
// Lapidação (12/10/2026): o servidor conta só os avisos PRINCIPAIS e do ESCOPO
// da conta da sessão (o Narciso: só Moema) e manda `escopo_avisos` e
// `rotina_abertos` na mesma resposta — o formato aqui não muda.
//
// Uso: const { carregando, erro, contagem, recarregar } = useFinAlertas({ enabled });
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { contarAlertas } from './finAuditoriaApi.js';

export const INTERVALO_MS = 5 * 60 * 1000;
export const FOCO_MIN_MS = 60 * 1000;

/**
 * Loja do contador, sem React (testável): { estado, assinar, atualizar }.
 * atualizar({ forcar, minIntervaloMs }): sem forcar, não busca se a última
 * TENTATIVA foi há menos de minIntervaloMs. Pedido em voo é reaproveitado;
 * forcar com pedido em voo agenda mais um ao fim (o resultado em voo pode ser
 * de antes da mudança de status).
 */
export function criarLojaContagem({ buscar, agora = () => Date.now() } = {}) {
  let estado = { carregando: false, erro: '', erroCodigo: '', contagem: null, semAcesso: false, carregadoEm: 0, tentadoEm: 0 };
  let emVoo = null;
  let deNovo = false;
  const ouvintes = new Set();
  const emitir = () => { for (const fn of ouvintes) fn(estado); };

  async function rodar() {
    estado = { ...estado, carregando: true, tentadoEm: agora() };
    emitir();
    try {
      const c = await buscar();
      if (!c || typeof c !== 'object') throw Object.assign(new Error('Resposta inesperada da contagem.'), { code: 'resposta' });
      estado = { ...estado, carregando: false, erro: '', erroCodigo: '', contagem: c, semAcesso: false, carregadoEm: agora() };
    } catch (err) {
      if (err && err.status === 403) {
        estado = { ...estado, carregando: false, erro: '', erroCodigo: '', contagem: null, semAcesso: true };
      } else {
        // mantém a última contagem: falha de leitura não vira "zero alertas"
        estado = { ...estado, carregando: false, erro: (err && err.message) || String(err), erroCodigo: (err && err.code) || 'erro' };
      }
    }
    emitir();
  }

  function atualizar({ forcar = false, minIntervaloMs = 0 } = {}) {
    if (emVoo) {
      if (forcar) deNovo = true;
      return emVoo;
    }
    if (!forcar && estado.tentadoEm && agora() - estado.tentadoEm < minIntervaloMs) return Promise.resolve(estado);
    emVoo = (async () => {
      try {
        await rodar();
        while (deNovo) { deNovo = false; await rodar(); }
      } finally {
        emVoo = null;
      }
      return estado;
    })();
    return emVoo;
  }

  return {
    estado: () => estado,
    assinar(fn) { ouvintes.add(fn); return () => ouvintes.delete(fn); },
    atualizar,
  };
}

// ─── Loja única do app ─────────────────────────────────────────────────────
const loja = criarLojaContagem({ buscar: () => contarAlertas() });
let ativos = 0;
let timer = null;
const visivel = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';
const aoFocar = () => { if (visivel()) loja.atualizar({ minIntervaloMs: FOCO_MIN_MS }); };

function ligar() {
  ativos += 1;
  if (ativos > 1 || typeof window === 'undefined') return;
  timer = setInterval(() => { if (visivel()) loja.atualizar({ minIntervaloMs: FOCO_MIN_MS }); }, INTERVALO_MS);
  window.addEventListener('focus', aoFocar);
  document.addEventListener('visibilitychange', aoFocar);
}
function desligar() {
  ativos = Math.max(0, ativos - 1);
  if (ativos > 0 || typeof window === 'undefined') return;
  clearInterval(timer);
  timer = null;
  window.removeEventListener('focus', aoFocar);
  document.removeEventListener('visibilitychange', aoFocar);
}

export function useFinAlertas({ enabled = true } = {}) {
  const [estado, setEstado] = useState(loja.estado());
  useEffect(() => loja.assinar(setEstado), []);
  useEffect(() => {
    if (!enabled) return undefined;
    ligar();
    // primeiro uso busca; se outra parte da tela buscou há < 1 min, reaproveita
    loja.atualizar({ minIntervaloMs: FOCO_MIN_MS });
    return desligar;
  }, [enabled]);
  const recarregar = useCallback((opts = {}) => loja.atualizar({ forcar: !opts.seMaisVelhoQueMs, minIntervaloMs: opts.seMaisVelhoQueMs || 0 }), []);
  if (!enabled) return { carregando: false, erro: '', erroCodigo: '', contagem: null, recarregar };
  return {
    carregando: estado.carregando && !estado.contagem,
    atualizando: estado.carregando,
    erro: estado.erro,
    erroCodigo: estado.erroCodigo,
    contagem: estado.semAcesso ? null : estado.contagem,
    carregadoEm: estado.carregadoEm,
    recarregar,
  };
}

export default useFinAlertas;
